import { type FC, useState } from 'react';
import { RiCloseLine } from 'react-icons/ri';
import SelectDropDown from '@/components/UI/SelectDropDown';
import MenuContainer from '@/components/UI/SelectDropDown/Menus/MenuContainer';
import Button from '@/components/UI/Button/Button';
import Spinner from '@/components/UI/Spinner';
import { useUserTableViews } from '@/utils/userTableSettings/UserTableSettingsContext';
import { _isNotEmpty } from '@/littledash';
import { useModalAction } from '@/utils/modal';
import { errorToast } from '@/helpers';
import styles from './ViewSelector.module.scss';
import type { UserTableView, UserTableViewSettings, UserTableViewTableName } from '@/model/UserTableView.model.ts';

interface ViewSelectorProps {
  tableName: UserTableViewTableName;
  /** Callback to get current table settings when saving a new view */
  getCurrentSettings: () => UserTableViewSettings;
  /** Called when user selects a different view */
  onViewChange: (settings: UserTableViewSettings | undefined) => void;
}

interface ViewSelectorMenuProps {
  tableName: UserTableViewTableName;
  getCurrentSettings: () => UserTableViewSettings;
  onViewChange: (settings: UserTableViewSettings | undefined) => void;
  closeMenu?: () => void;
}

const ViewSelectorMenu: FC<ViewSelectorMenuProps> = ({ tableName, getCurrentSettings, onViewChange, closeMenu }) => {
  const { getViews, getActiveViewApiId, deleteView, setActiveView, clearActiveView } = useUserTableViews();
  const { openModal } = useModalAction();
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const views = getViews(tableName);
  const activeViewApiId = getActiveViewApiId(tableName);

  const handleSelectDefault = async () => {
    if (isSelecting) {
      return;
    }
    if (activeViewApiId !== undefined) {
      setIsSelecting(true);
      const success = await clearActiveView(tableName);
      if (success) {
        onViewChange(undefined);
        closeMenu?.();
      } else {
        errorToast('Failed to switch view. Please try again.');
      }
      setIsSelecting(false);
    } else {
      closeMenu?.();
    }
  };

  const handleSelectView = async (view: UserTableView) => {
    if (isSelecting) {
      return;
    }
    if (view.api_id !== activeViewApiId) {
      setIsSelecting(true);
      const success = await setActiveView(tableName, view.api_id);
      if (success) {
        onViewChange(view.settings);
        closeMenu?.();
      } else {
        errorToast('Failed to switch view. Please try again.');
      }
      setIsSelecting(false);
    } else {
      // Already active - reapply the view's settings to reset any local changes
      onViewChange(view.settings);
      closeMenu?.();
    }
  };

  const handleDeleteView = async (view: UserTableView, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDeleting !== null) {
      return;
    }
    setIsDeleting(view.api_id);
    const success = await deleteView(view.api_id);
    if (success) {
      if (view.api_id === activeViewApiId) {
        onViewChange(undefined);
      }
    } else {
      errorToast('Failed to delete view. Please try again.');
    }
    setIsDeleting(null);
  };

  const handleSaveNewView = () => {
    if (views.length >= 10) {
      return;
    }
    const currentSettings = getCurrentSettings();
    closeMenu?.();
    openModal('SAVE_TABLE_VIEW', {
      tableName,
      settings: currentSettings,
      onViewSaved: onViewChange,
    });
  };

  const isAtViewLimit = views.length >= 10;

  return (
    <MenuContainer width={320}>
      <div className={styles.header}>Saved Views</div>
      <div className={styles.viewList}>
        <div
          className={`${styles.viewItem} ${activeViewApiId === undefined ? styles.active : ''}`}
          onClick={handleSelectDefault}
          data-testid="view-selector__default"
        >
          <span className={styles.viewName}>Default</span>
        </div>

        {_isNotEmpty(views) && (
          <>
            {views.map((view) => (
              <div
                key={view.api_id}
                className={`${styles.viewItem} ${view.api_id === activeViewApiId ? styles.active : ''}`}
                onClick={() => handleSelectView(view)}
                data-testid={`view-selector__view-${view.api_id}`}
              >
                <span className={styles.viewName}>{view.name}</span>
                <div className={styles.viewActions}>
                  <div
                    className={`${styles.deleteButton} ${isDeleting === view.api_id ? styles.disabled : ''}`}
                    onClick={(e) => handleDeleteView(view, e)}
                    data-testid={`view-selector__delete-${view.api_id}`}
                    aria-label={`Delete ${view.name}`}
                  >
                    {isDeleting === view.api_id ? <Spinner size="small" color="gray" /> : <RiCloseLine size={16} />}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <div className={styles.saveSection}>
        <Button
          onClick={handleSaveNewView}
          className="w-100 f6"
          plain
          testId="view-selector__save-button"
          disabled={isAtViewLimit}
          tooltip={isAtViewLimit ? 'Maximum of 10 views per table' : undefined}
        >
          Save current view
        </Button>
      </div>
    </MenuContainer>
  );
};

const ViewSelector: FC<ViewSelectorProps> = ({ tableName, getCurrentSettings, onViewChange }) => {
  const { getViews, getActiveViewApiId, isLoaded } = useUserTableViews();

  const views = getViews(tableName);
  const activeViewApiId = getActiveViewApiId(tableName);
  const activeView = views.find((v) => v.api_id === activeViewApiId);
  const displayName = activeView?.name ?? 'Default';

  if (!isLoaded) {
    return null;
  }

  return (
    <SelectDropDown
      title={displayName}
      className={`plain f6 ${styles.trigger}`}
      alignMenu="right"
      testId="view-selector"
    >
      <ViewSelectorMenu tableName={tableName} getCurrentSettings={getCurrentSettings} onViewChange={onViewChange} />
    </SelectDropDown>
  );
};

export default ViewSelector;
